import { useState, useEffect, useCallback } from 'react';

/**
 * A recent concierge search query stored in localStorage.
 * Normalised text is used for deduplication — raw text is preserved for display.
 */
export interface RecentSearchQuery {
  text: string;
  timestamp: number;
}

const STORAGE_KEY = 'RecentConciergeQueries';
const MAX_ENTRIES = 3;

/**
 * Normalise a search string for deduplication:
 * trim → lowercase → collapse consecutive whitespace to a single space.
 */
export const normaliseSearchText = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ');

const loadRecents = (): RecentSearchQuery[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry: unknown): entry is RecentSearchQuery =>
        typeof (entry as RecentSearchQuery)?.text === 'string' &&
        typeof (entry as RecentSearchQuery)?.timestamp === 'number'
    );
  } catch {
    return [];
  }
};

const saveRecents = (recents: RecentSearchQuery[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recents));
  } catch {
    // localStorage full or unavailable — silently discard
  }
};

/**
 * Hook for managing recent concierge search queries in localStorage.
 *
 * Follows the same pattern as RecentSearchLocations in SearchBar/index.tsx:
 * read on mount, write on action, dedupe by normalised value, max N entries.
 */
export const useRecentSearches = () => {
  const [recents, setRecents] = useState<RecentSearchQuery[]>(loadRecents);

  // Re-read from localStorage on mount in case another tab wrote
  useEffect(() => {
    setRecents(loadRecents());
  }, []);

  /**
   * Add a query to recent searches. Dedupes by normalised text (keeping the
   * most recent timestamp) and caps the list at MAX_ENTRIES.
   */
  const addRecent = useCallback((rawText: string) => {
    const normalised = normaliseSearchText(rawText);
    if (!normalised) return;

    setRecents((prev) => {
      const entry: RecentSearchQuery = {
        text: rawText.trim(),
        timestamp: Date.now(),
      };

      // Remove any existing entry with the same normalised text, then prepend
      const filtered = prev.filter(
        (r) => normaliseSearchText(r.text) !== normalised
      );
      const updated = [entry, ...filtered].slice(0, MAX_ENTRIES);

      saveRecents(updated);
      return updated;
    });
  }, []);

  /**
   * Remove a specific entry from recent searches (by normalised match).
   */
  const removeRecent = useCallback((rawText: string) => {
    const normalised = normaliseSearchText(rawText);
    if (!normalised) return;

    setRecents((prev) => {
      const updated = prev.filter(
        (r) => normaliseSearchText(r.text) !== normalised
      );
      saveRecents(updated);
      return updated;
    });
  }, []);

  /**
   * Clear all recent searches.
   */
  const clearRecents = useCallback(() => {
    setRecents([]);
    saveRecents([]);
  }, []);

  return { recents, addRecent, removeRecent, clearRecents };
};
